import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Stack, Tooltip, Autocomplete, FormControlLabel, Switch } from '@mui/material';
import SaveButton from '../Components/SaveButton';
import axios from 'axios';
import { APIURL } from '../../../config';

// Added formList & flowList for future expansion; they can be empty arrays if not yet supplied
const Referral = ({ defaultGuid = '', comp_id, empList = [], formList = [], flowList = [] }) => {
  const [savedGuid, setSavedGuid] = useState(defaultGuid);
  const [companyGuid, setCompanyGuid] = useState(defaultGuid);
  const [isError, setIsError] = useState(false);
  const [isFormatError, setIsFormatError] = useState(false);
  const [isGuidTaken, setIsGuidTaken] = useState(false);
  const [guidHelper, setGuidHelper] = useState('');
  const [checkingGuid, setCheckingGuid] = useState(false);
  // Versioned setting fields (referral_setting table)
  const [savedCoordinator, setSavedCoordinator] = useState('');
  const [savedIncomingFormId, setSavedIncomingFormId] = useState('');
  const [savedOutgoingFormId, setSavedOutgoingFormId] = useState('');
  const [savedFlowId, setSavedFlowId] = useState('');
  const [coordinator, setCoordinator] = useState('');
  const [incomingFormId, setIncomingFormId] = useState('');
  const [outgoingFormId, setOutgoingFormId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [printIncludeReferrerInfo, setPrintIncludeReferrerInfo] = useState(true);
  const [savedPrintIncludeReferrerInfo, setSavedPrintIncludeReferrerInfo] = useState(true);

  // Subscribed forms for the company (used to populate Referral Form list)
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Published workflows for the company (used to populate Referral Flow list)
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);

  const [activeSetting, setActiveSetting] = useState(null); // stores last active version
  const [settingError, setSettingError] = useState(null);
  const [loadingSetting, setLoadingSetting] = useState(false);

  // Phone/email removed: now only using referral_setting versioned values + GUID.

  useEffect(() => {
    const fetchData = async () => {
      // Fetch active versioned referral setting (coordinator / form / flow)
      setLoadingSetting(true);
      try {
        const settingResp = await axios.get(`${APIURL}/referralSettings/${comp_id}/active`);
        const setting = settingResp.data;
        console.log(setting);

        setActiveSetting(setting);
        setCoordinator(setting.referral_coordinator_emp_id?.toString() || '');
        setIncomingFormId(setting.referral_form_incoming_id?.toString() || '');
        setOutgoingFormId(setting.referral_form_outgoing_id?.toString() || '');
        setFlowId(setting.referral_flow_id?.toString() || '');
        const rawPrint = setting.print_include_referrer_info;
        const parsedPrint = (rawPrint === null || rawPrint === undefined) ? true
          : (typeof rawPrint === 'string' ? rawPrint !== '0' && rawPrint.toLowerCase() !== 'false' : !!rawPrint);
        setPrintIncludeReferrerInfo(parsedPrint);
        setSavedPrintIncludeReferrerInfo(parsedPrint);
        // sync GUID from active setting row
        if (setting.company_guid) {
          setCompanyGuid(setting.company_guid);
          setSavedGuid(setting.company_guid);
        }
        setSavedCoordinator(setting.referral_coordinator_emp_id?.toString() || '');
        setSavedIncomingFormId(setting.referral_form_incoming_id?.toString() || '');
        setSavedOutgoingFormId(setting.referral_form_outgoing_id?.toString() || '');
        setSavedFlowId(setting.referral_flow_id?.toString() || '');
        const rawPrint2 = setting.print_include_referrer_info;
        const parsedPrint2 = (rawPrint2 === null || rawPrint2 === undefined) ? true
          : (typeof rawPrint2 === 'string' ? rawPrint2 !== '0' && rawPrint2.toLowerCase() !== 'false' : !!rawPrint2);
        setSavedPrintIncludeReferrerInfo(parsedPrint2);
      } catch (err) {
        if (err?.response?.status === 404) {
          // No active setting yet (initial create scenario)
          setActiveSetting(null);
        } else {
          console.error('Active referral setting fetch error', err);
          setSettingError('Unable to load referral setting');
        }
      } finally {
        setLoadingSetting(false);
      }
    };
    fetchData();
  }, [comp_id]);

  // Fetch subscribed forms for this company to populate the Referral Form field
  useEffect(() => {
    let cancelled = false;
    const fetchForms = async () => {
      try {
        setLoadingForms(true);
        const resp = await axios.get(`${APIURL}/task_form/subscribed?company_id=${comp_id}`);
        if (!cancelled) setAvailableForms(resp?.data || []);
      } catch (err) {
        if (!cancelled) setAvailableForms([]);
        console.error('Error fetching subscribed forms', err);
      } finally {
        if (!cancelled) setLoadingForms(false);
      }
    };

    // Fetch published workflows for this company to populate the Referral Flow field
    const fetchWorkflows = async () => {
      try {
        setLoadingWorkflows(true);
        const resp = await axios.get(`${APIURL}/workflows`, {
          params: { compID: comp_id },
        });
        if (!cancelled) setAvailableWorkflows(resp?.data.filter((wf) => wf.wf_status === 1) || []);
      } catch (err) {
        if (!cancelled) setAvailableWorkflows([]);
        console.error('Error fetching published workflows', err);
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    };

    fetchForms();
    fetchWorkflows();
    return () => { cancelled = true; };
  }, [comp_id]);

  const handleChange = (e) => {
    const value = e.target.value;
    setCompanyGuid(value);
    const formatInvalid = value.length > 0 && !/^[a-z0-9-]+$/.test(value);
    setIsFormatError(formatInvalid);
  };

  // Debounced GUID availability/validity check
  useEffect(() => {
    let timer;
    const run = async () => {
      const val = (companyGuid || '').trim();
      const currentVal = val;
      if (!val) {
        // No entry: don't show error; backend can generate on create
        setIsGuidTaken(false);
        setGuidHelper('');
        setIsError(false);
        return;
      }
      if (isFormatError) {
        setIsGuidTaken(false);
        setGuidHelper('Only lowercase letters, numbers, and hyphens allowed');
        setIsError(true);
        return;
      }
      if (val === savedGuid) {
        setIsGuidTaken(false);
        setGuidHelper('');
        setIsError(false);
        return;
      }
      setCheckingGuid(true);
      try {
        const resp = await axios.get(`${APIURL}/referralSettings/guid/verify/${encodeURIComponent(val)}`);
        // Ignore if input changed while awaiting
        if ((companyGuid || '').trim() !== currentVal) return;
        const exists = resp?.data?.exists;
        const ownerId = resp?.data?.company_id;
        if (exists && String(ownerId) !== String(comp_id)) {
          setIsGuidTaken(true);
          setGuidHelper('GUID already in use');
          setIsError(true);
        } else {
          setIsGuidTaken(false);
          setGuidHelper('');
          setIsError(false);
        }
      } catch (err) {
        if ((companyGuid || '').trim() !== currentVal) return;
        // 404 -> available
        if (err?.response?.status === 404) {
          setIsGuidTaken(false);
          setGuidHelper('');
          setIsError(false);
        } else {
          // On other errors, do not block saving
          setIsGuidTaken(false);
          setGuidHelper('');
          setIsError(false);
        }
      } finally {
        if ((companyGuid || '').trim() === currentVal) setCheckingGuid(false);
      }
    };
    timer = setTimeout(run, 350);
    return () => clearTimeout(timer);
  }, [companyGuid, savedGuid, comp_id, isFormatError]);
  // Removed phone/email handlers
  const handleCoordinatorChange = (e, value) => {
    setCoordinator(value ? String(value.emp_id) : '');
  };
  const resolveFormId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    const id = value.form_id ?? value.id ?? value.value;
    return id ? String(id) : '';
  };
  const handleIncomingFormChange = (e, value) => {
    setIncomingFormId(resolveFormId(value));
  };
  const handleOutgoingFormChange = (e, value) => {
    setOutgoingFormId(resolveFormId(value));
  };
  const handleFlowChange = (e, value) => {
    if (value == null) {
      setFlowId('');
      return;
    }
    if (typeof value === 'string') {
      setFlowId(value);
      return;
    }
    const id = value.wf_id ?? value.id ?? value.value;
    setFlowId(id ? String(id) : '');
  };

  const handleSave = async () => {
    if (isError) return;
    try {
      // Unified upsert to referralSettings (handles create/update/version)
      const settingChanged = (
        coordinator !== savedCoordinator || incomingFormId !== savedIncomingFormId || outgoingFormId !== savedOutgoingFormId || flowId !== savedFlowId ||
        printIncludeReferrerInfo !== savedPrintIncludeReferrerInfo
      );
      const guidChanged = companyGuid !== savedGuid;
      if (!settingChanged && !guidChanged) return;

      const payload = {};
      if (guidChanged) payload.company_guid = companyGuid;
      if (coordinator !== savedCoordinator) payload.referral_coordinator_emp_id = coordinator || null;
      if (incomingFormId !== savedIncomingFormId) payload.referral_form_incoming_id = incomingFormId || null;
      if (outgoingFormId !== savedOutgoingFormId) payload.referral_form_outgoing_id = outgoingFormId || null;
      if (flowId !== savedFlowId) payload.referral_flow_id = flowId || null;
      if (printIncludeReferrerInfo !== savedPrintIncludeReferrerInfo) payload.print_include_referrer_info = printIncludeReferrerInfo;

      const resp = await axios.put(`${APIURL}/referralSettings/${comp_id}`, payload);
      const newSetting = resp.data?.setting || resp.data?.current || null;
      if (newSetting) {
        setActiveSetting(newSetting);
        setSavedCoordinator(newSetting.referral_coordinator_emp_id?.toString() || coordinator);
        setSavedIncomingFormId(newSetting.referral_form_incoming_id?.toString() || incomingFormId);
        setSavedOutgoingFormId(newSetting.referral_form_outgoing_id?.toString() || outgoingFormId);
        setSavedFlowId(newSetting.referral_flow_id?.toString() || flowId);
        setSavedPrintIncludeReferrerInfo(printIncludeReferrerInfo);
        if (newSetting.company_guid) {
          setSavedGuid(newSetting.company_guid);
          setCompanyGuid(newSetting.company_guid);
        } else if (guidChanged) {
          setSavedGuid(companyGuid);
        }
      } else {
        // Fallback to refetch active
        try {
          const check = await axios.get(`${APIURL}/referralSettings/${comp_id}/active`);
          const s = check.data;
          setActiveSetting(s);
          setSavedCoordinator(s.referral_coordinator_emp_id?.toString() || coordinator);
          setSavedIncomingFormId(s.referral_form_incoming_id?.toString() || incomingFormId);
          setSavedOutgoingFormId(s.referral_form_outgoing_id?.toString() || outgoingFormId);
          setSavedFlowId(s.referral_flow_id?.toString() || flowId);
          setSavedPrintIncludeReferrerInfo(printIncludeReferrerInfo);
          if (s.company_guid) { setSavedGuid(s.company_guid); setCompanyGuid(s.company_guid); }
        } catch (_) { /* ignore */ }
      }
    } catch (error) {
      console.error('Save error', error);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid size={4}>
          <Stack spacing={1}>
            <Typography variant="h5">Referral</Typography>
            <Typography variant="body1" sx={{ color: 'gray' }}>
              Set up referral details for the company
            </Typography>
          </Stack>
        </Grid>
        <Grid size={8}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Tooltip title="Unique identifier in referral URL, e.g. referral/[GUID]">
                <TextField
                  label="Company GUID"
                  value={companyGuid}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Enter or edit company GUID"
                  error={isError}
                  helperText={isError ? (guidHelper || 'Invalid or unavailable') : ''}
                />
              </Tooltip>
            </Grid>
            {/* Phone / Email fields removed – now managed elsewhere */}
            <Grid size={6}>
              <Autocomplete
                options={empList}
                getOptionLabel={(option) => option.emp_first_name + ' ' + option.emp_last_name || ''}
                value={empList.find(e => String(e.emp_id) === coordinator) || null}
                onChange={handleCoordinatorChange}
                renderInput={(params) => <TextField {...params} label="Referral Coordinator" size="small" variant="outlined" fullWidth />}
              />
            </Grid>
            <Grid size={6}>
              <Tooltip title="Form used when your company RECEIVES referrals (incoming).">
                <Autocomplete
                  options={(formList && formList.length ? formList : availableForms)}
                  loading={loadingForms}
                  getOptionLabel={(o) => (o?.form_name || o?.name || o?.label || '')}
                  value={(formList && formList.length ? formList : availableForms).find(f => String(f.form_id ?? f.id ?? f.value) === incomingFormId) || null}
                  onChange={handleIncomingFormChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Incoming Referral Form"
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder={(formList && formList.length ? '' : (availableForms.length ? '' : 'Enter form id'))}
                    />
                  )}
                  freeSolo={(formList && formList.length ? false : (availableForms.length === 0))}
                />
              </Tooltip>
            </Grid>
            <Grid size={6}>
              <Tooltip title="Form used when your company SENDS referrals (outgoing).">
                <Autocomplete
                  options={(formList && formList.length ? formList : availableForms)}
                  loading={loadingForms}
                  getOptionLabel={(o) => (o?.form_name || o?.name || o?.label || '')}
                  value={(formList && formList.length ? formList : availableForms).find(f => String(f.form_id ?? f.id ?? f.value) === outgoingFormId) || null}
                  onChange={handleOutgoingFormChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Outgoing Referral Form"
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder={(formList && formList.length ? '' : (availableForms.length ? '' : 'Enter form id'))}
                    />
                  )}
                  freeSolo={(formList && formList.length ? false : (availableForms.length === 0))}
                />
              </Tooltip>
            </Grid>
            <Grid size={6}>
              <Autocomplete
                options={(flowList && flowList.length ? flowList : availableWorkflows)}
                loading={loadingWorkflows}
                getOptionLabel={(option) => option.wf_title}
                value={(flowList && flowList.length ? flowList : availableWorkflows).find(f => String(f.wf_id) === flowId) || null}
                onChange={handleFlowChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Referral Flow"
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder={flowList.length ? '' : 'Select Flow'}
                  />
                )}
                freeSolo={(flowList && flowList.length ? false : (availableWorkflows.length === 0))}
              />
            </Grid>
            <Grid size={12}>
              <Tooltip title="When printing a referral form, include the referring org, client name, and address at the top of the printout.">
                <FormControlLabel
                  control={
                    <Switch
                      checked={printIncludeReferrerInfo}
                      onChange={(e) => setPrintIncludeReferrerInfo(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Print referrer info header"
                />
              </Tooltip>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <SaveButton
              onClick={handleSave}
              disabled={
                isError || loadingSetting ||
                (companyGuid === savedGuid &&
                  coordinator === savedCoordinator &&
                  incomingFormId === savedIncomingFormId &&
                  outgoingFormId === savedOutgoingFormId &&
                  flowId === savedFlowId &&
                  printIncludeReferrerInfo === savedPrintIncludeReferrerInfo)
              }
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Referral;
